import { createMultipleLayers } from "@/api/hooks/layer";
import type { LayerCreateUnion } from "@/layers";
import { useAppDispatch } from "@/reducer";
import { setMapOffsetTimeProps, setMapProjectionProps, setMapTitleProps, useMap } from "@/reducer/MapsState";
import type { MapId } from "@/types/type-utils";
import type { MapProjection } from "@mm/metx-workbench.meteomatics.com";
import { isEmpty } from "lodash";
import { useMemo } from "react";
import { batch } from "react-redux";
import { BufferName, useLocalClipboard } from "../ManageActionPanel/LocalClipboardContext";
import { ManageActionPanel } from "../ManageActionPanel/ManageActionPanel";

const IGNORABLE_KEYS = ["id", "id_cartographicmap", "id_profile", "time_created", "time_updated"];

type LayersActionPanelProps = {
  id: MapId;
  clearMap: () => void;
  mapProjection: MapProjection;
};

type CopyPasteMapData = {
  mapProjection: MapProjection;
  layers: LayerCreateUnion[];
  timeOffset: number;
  title: string | null;
};

function MapActionPanel({ id, mapProjection, clearMap }: LayersActionPanelProps) {
  const map = useMap(id.map);
  const descriptionLayers = map.layers;
  const timeOffset = map.time_offset_mins;
  const mapTitle = map.title;

  const dispatch = useAppDispatch();

  const { storeData, readData, hasData } = useLocalClipboard<CopyPasteMapData>(); // Create Type

  const hasLayers = useMemo(() => !isEmpty(descriptionLayers), [descriptionLayers]);

  function copyMap() {
    if (descriptionLayers) {
      // Cleanup layers for storing in "clipboard"
      const cleanedLayers = descriptionLayers.map<LayerCreateUnion>((layer) => {
        const cleanedKeys = Object.keys(layer).filter((key: string) => IGNORABLE_KEYS.indexOf(key) === -1);
        const cleanedLayer = cleanedKeys.reduce((acc: any, val: string) => {
          acc[val] = (layer as unknown as Record<string, unknown>)[val];
          return acc;
        }, {});

        return cleanedLayer;
      });
      const mapData = {
        mapProjection: mapProjection,
        layers: cleanedLayers,
        timeOffset: timeOffset,
        title: mapTitle,
      };
      storeData(BufferName.MapLayers, mapData);
    } else {
      console.error(`Layer descriptions from map with id=${id.map} missing`);
    }
  }

  function replaceLayers() {
    const newMapData: CopyPasteMapData = readData(BufferName.MapLayers);
    batch(() => {
      dispatch(createMultipleLayers({ mapId: id.map, data: newMapData.layers, profileId: id.profile }));
      dispatch(setMapProjectionProps({ id: id.map, props: newMapData.mapProjection }));
      dispatch(setMapTitleProps({ id: id.map, title: newMapData.title }));
      dispatch(setMapOffsetTimeProps({ id: id.map, timeOffsetMinutes: newMapData.timeOffset }));
    });
  }

  return (
    <ManageActionPanel
      onCopy={() => copyMap()}
      onReplace={() => replaceLayers()}
      onRemove={clearMap}
      pastDisabled={!hasData(BufferName.MapLayers)}
      disabled={!hasLayers}
    />
  );
}

export { MapActionPanel };
